import { StyledCreditsLimitBanner } from '@common/credits/ProfessionalEdition/CreditsLimitBanner';
import { useProfessionalCreditStatus } from '@common/credits/ProfessionalEdition/hooks';
import { useWorkspaceOntologyFlags } from '@common/teams/useTeamOntologyFlags';
import { ProjectSourceType } from '@common/types';
import { Entity } from '@common/types/Entity';
import { VAPP_Private_Routes } from '@common/url';
import { useIsVappImportProjectEnabled, useUserWorkspace } from '@common/VerticalApps/hooks';
import { useReconcileVerticalAppsProjectPreferences } from '@common/VerticalApps/useReconcileVerticalAppsProjectPreferences';
import { useVerticalAppsProjectList } from '@common/VerticalApps/useVerticalAppsProjectList';
import { useVerticalAppsSideNavProjectsPreference } from '@common/VerticalApps/useVerticalAppsSideNavProjectsPreference';
import { withGuard } from '@common/withGuard';
import { ChevronDownIcon, DownloadDIcon, OntologyIcon, PlusIcon, ProjectIcon, SearchSmIcon } from '@prophecy/icons';
import { theme } from '@prophecy/theme';
import { Button, Display, Dropdown, Input, Spinner, Stack, Text, Tooltip } from '@prophecy/ui-v3';
import { InputSize } from '@prophecy/ui-v3/Input/types';
import { usePersistentCallback } from '@prophecy/utils/react/hooks';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import useCreateVAppProject from '../Home/useCreateVAppProject';
import { openCreateProjectDialog } from './CreateProjectDialog';
import {
  computeProjectListingFacets,
  EMPTY_PROJECT_LISTING_FILTERS,
  filterProjectsByListingFilters,
  isTypesProject,
  type ProjectListingFilters
} from './entityFilterUtils';
import {
  PROJECT_LIST_STATE_HEIGHT,
  Root,
  StyledContainer,
  StyledHeaderRow,
  StyledProjectGrid,
  StyledSearchInput,
  StyledSearchRow,
  StyledTab,
  StyledTabBar,
  StyledTabGroup
} from './listingChrome';
import { ProjectCard } from './ProjectCard';
import { ProjectListingFilter } from './ProjectListingFilter';
import { useDeleteVAppProject } from './useDeleteVAppProject';

const TABS = [
  { key: 'all', label: 'All' },
  { key: 'yours', label: 'Your projects' },
  { key: 'types', label: 'Types' }
] as const;

type TabKey = (typeof TABS)[number]['key'];

const TYPES_PROJECT_ADMIN_ONLY = 'Only workspace admins can create a Types project';

export default function ProjectListing() {
  const navigate = useNavigate();
  const { isProjectPinned, togglePinnedProject } = useVerticalAppsSideNavProjectsPreference();
  const { currentWorkspace } = useUserWorkspace();

  const [activeTab, setActiveTab] = useState<TabKey>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [entityFilters, setEntityFilters] = useState<ProjectListingFilters>(EMPTY_PROJECT_LISTING_FILTERS);

  const { projects, isLoading, isSuccess, uid, projectNames } = useVerticalAppsProjectList();
  const { isOntologyDisabled } = useWorkspaceOntologyFlags();
  const visibleTabs = isOntologyDisabled ? TABS.filter(({ key }) => key !== 'types') : TABS;
  const currentTab: TabKey = isOntologyDisabled && activeTab === 'types' ? 'all' : activeTab;

  // Workspace + tab + search narrowing. Entity-kind facet counts are derived from this set.
  const searchedProjects = useMemo(() => {
    const inWorkspace = projects.filter(
      (project) => project.team?._id === currentWorkspace?._id && !(isOntologyDisabled && isTypesProject(project))
    );
    const byTab = inWorkspace.filter((project) => {
      if (currentTab === 'yours') return project.createdBy === uid;
      if (currentTab === 'types') return isTypesProject(project);
      return true;
    });
    const query = searchQuery.trim().toLowerCase();
    // Match the project name OR any contained entity's name (e.g. a CDM, data mapping, pipeline or
    // analysis), so users can find "all projects containing CDM X" — the entity names are already in
    // the project list payload, so no extra fetch is needed.
    return query
      ? byTab.filter(
          (project) =>
            project.name.toLowerCase().includes(query) ||
            (project.entities ?? []).some((entity) =>
              (entity.items ?? []).some((item) => item.name?.toLowerCase().includes(query))
            )
        )
      : byTab;
  }, [projects, currentWorkspace?._id, isOntologyDisabled, currentTab, uid, searchQuery]);

  const entityFacets = useMemo(() => computeProjectListingFacets(searchedProjects), [searchedProjects]);

  const filteredProjects = useMemo(
    () => filterProjectsByListingFilters(searchedProjects, entityFilters),
    [searchedProjects, entityFilters]
  );

  // This page loads the authoritative project list, so reconcile stale preference entries here.
  useReconcileVerticalAppsProjectPreferences();

  const handleDeleteProject = useDeleteVAppProject();

  const isVappImportProjectEnabled = useIsVappImportProjectEnabled();
  const { isBlocked, blockedReason } = useProfessionalCreditStatus();
  const {
    createNamedProject,
    createTypesProject,
    getDefaultProjectName,
    getDefaultTypesProjectName,
    isCreatingProject
  } = useCreateVAppProject();
  const isWorkspaceAdmin = Boolean(currentWorkspace?.admins.some((admin) => admin._id === uid));

  useEffect(() => {
    setEntityFilters(EMPTY_PROJECT_LISTING_FILTERS);
  }, [currentTab, currentWorkspace?._id]);

  const handlePinProject = useCallback(
    (projectId: string) => {
      const project = projects.find((projectItem) => projectItem._id === projectId);
      if (!project) return;

      togglePinnedProject({
        projectId: project._id,
        name: project.name,
        workspaceId: project.team?._id
      });
    },
    [projects, togglePinnedProject]
  );

  const handleCreateProject = usePersistentCallback(
    withGuard(
      async () => {
        const projectId = await openCreateProjectDialog({
          kind: 'project',
          defaultName: getDefaultProjectName(),
          existingProjectNames: projectNames,
          createProject: createNamedProject
        });
        if (projectId) navigate(VAPP_Private_Routes.Project_Detail.getUrl({ uid: projectId }));
      },
      { reason: blockedReason, blocked: isBlocked }
    )
  );

  const handleImportProject = usePersistentCallback(
    withGuard(
      () => navigate(VAPP_Private_Routes.Project_Create.getUrl(undefined, { projectType: ProjectSourceType.imported })),
      {
        reason: blockedReason,
        blocked: isBlocked
      }
    )
  );

  const handleCreateTypesProject = usePersistentCallback(
    withGuard(
      async () => {
        const projectId = await openCreateProjectDialog({
          kind: 'types',
          defaultName: getDefaultTypesProjectName(),
          existingProjectNames: projectNames,
          createProject: createTypesProject
        });
        if (projectId) navigate(VAPP_Private_Routes.Project_Detail.getUrl({ uid: projectId }));
      },
      { reason: blockedReason, blocked: isBlocked }
    )
  );

  const handleOpenEntity = useCallback(
    (projectId: string, entity: Entity, name: string) => {
      navigate(VAPP_Private_Routes.Project_Detail.getUrl({ uid: projectId }, { entity: entity.toLowerCase(), name }));
    },
    [navigate]
  );

  const handleOpenProject = usePersistentCallback((projectId: string) => {
    navigate(VAPP_Private_Routes.Project_Detail.getUrl({ uid: projectId }));
  });

  // The default name and the duplicate check both read the loaded project list.
  const isProjectCreateEnabled = Boolean(!isCreatingProject && isSuccess);
  const isTypesProjectEnabled = Boolean(isWorkspaceAdmin && !isCreatingProject && isSuccess);

  return (
    <Root>
      <StyledCreditsLimitBanner />
      <Stack grow='1' align='center' alignY='center'>
        <StyledContainer>
          <Stack gap={theme.spaces.x20}>
            <StyledHeaderRow>
              <Display level='xs' weight={theme.fontWeight.semiBold} tone={theme.colors.gray900}>
                All Projects
              </Display>
              <Stack direction='horizontal' gap={theme.spaces.x16}>
                {isVappImportProjectEnabled && (
                  <Button
                    variant='tertiary'
                    size='s'
                    onClick={handleImportProject}
                    iconPlacement='left'
                    icon={<DownloadDIcon type='default' />}
                    data-test-id='create-entity-import-project-button'>
                    Import
                  </Button>
                )}
                {isOntologyDisabled ? (
                  <Button
                    variant='secondaryGray'
                    size='s'
                    icon={<PlusIcon type='default' />}
                    iconPlacement='left'
                    disabled={!isProjectCreateEnabled}
                    onClick={handleCreateProject}
                    data-test-id='project-listing-create-project-button'>
                    Create Project
                  </Button>
                ) : (
                  <Dropdown
                    placement='bottom'
                    align='end'
                    data-test-id='project-listing-create-menu'
                    overlay={
                      <>
                        <Dropdown.Item
                          disabled={!isProjectCreateEnabled}
                          data-test-id='project-listing-create-project-item'
                          onClick={handleCreateProject}>
                          <Stack direction='horizontal' alignY='center' gap={theme.spaces.x8}>
                            <ProjectIcon type='extended' color={theme.colors.gray500} />
                            <Text level='sm' tone={theme.colors.gray900}>
                              Project
                            </Text>
                          </Stack>
                        </Dropdown.Item>
                        <Tooltip title={isWorkspaceAdmin ? undefined : TYPES_PROJECT_ADMIN_ONLY}>
                          <Dropdown.Item
                            disabled={!isTypesProjectEnabled}
                            data-test-id='project-listing-create-types-project-item'
                            onClick={handleCreateTypesProject}>
                            <Stack direction='horizontal' alignY='center' gap={theme.spaces.x8}>
                              <OntologyIcon
                                type='extended'
                                color={isTypesProjectEnabled ? theme.colors.pink600 : undefined}
                              />
                              <Text level='sm'>Types project</Text>
                            </Stack>
                          </Dropdown.Item>
                        </Tooltip>
                      </>
                    }>
                    <Button
                      variant='secondaryGray'
                      size='s'
                      icon={<PlusIcon type='default' />}
                      iconPlacement='left'
                      data-test-id='project-listing-create-project-button'>
                      <Stack direction='horizontal' alignY='center' gap={theme.spaces.x2}>
                        Create New
                        <ChevronDownIcon type='default' />
                      </Stack>
                    </Button>
                  </Dropdown>
                )}
              </Stack>
            </StyledHeaderRow>

            <StyledTabBar>
              <StyledTabGroup>
                {visibleTabs.map(({ key, label }) => (
                  <StyledTab key={key} $active={currentTab === key} onClick={() => setActiveTab(key)}>
                    {label}
                  </StyledTab>
                ))}
              </StyledTabGroup>

              <StyledSearchRow>
                <StyledSearchInput>
                  <Input
                    placeholder='Search projects'
                    value={searchQuery}
                    onChange={(value) => setSearchQuery(value)}
                    icon={<SearchSmIcon type='default' />}
                    iconPlacement='left'
                    data-test-id='project-listing-search-input'
                    inputSize={InputSize.s}
                  />
                </StyledSearchInput>
                <ProjectListingFilter facets={entityFacets} filters={entityFilters} onChange={setEntityFilters} />
              </StyledSearchRow>
            </StyledTabBar>

            {isLoading ? (
              <Stack alignY='center' align='center' height={PROJECT_LIST_STATE_HEIGHT}>
                <Spinner />
              </Stack>
            ) : filteredProjects.length === 0 ? (
              <Stack alignY='center' align='center' height={PROJECT_LIST_STATE_HEIGHT}>
                <Text level='sm' tone={theme.colors.gray500}>
                  No projects found
                </Text>
              </Stack>
            ) : (
              <StyledProjectGrid>
                {filteredProjects.map((project) => (
                  <ProjectCard
                    key={project._id}
                    project={project}
                    onClick={handleOpenProject}
                    onOpenEntity={handleOpenEntity}
                    isPinned={isProjectPinned(project._id)}
                    onPinProject={handlePinProject}
                    onDeleteProject={handleDeleteProject}
                  />
                ))}
              </StyledProjectGrid>
            )}
          </Stack>
        </StyledContainer>
      </Stack>
    </Root>
  );
}
